// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.
import { createAsyncThunk } from "@reduxjs/toolkit";

import axiosInstance from "../../services/axiosInstance";
import { apiCall } from "../utils/utils";

import { uploadImageAndRefresh } from "../utils/imageCache";

import {
  selectStudioUUIDByEquipmentUUID,
  selectAllEquipmentInsideGroups,
} from "./equipmentSelectors";

import {
  wsStudioEquipmentDesiredState,
  wsStudioEquipmentAction,
} from "../../../web-utils/src/store/Websocket/WebsocketActions";

import { getMatchingProtocols } from "./equipmentUtils";

const randomUUID = "81855524-3803-42c6-8e37-bcf5d0b15e85"; // Used as a placeholder UUID when removing connections, as the backend requires a non-empty UUID to process the update. This should ideally be handled more elegantly in the backend to avoid potential conflicts.

// get block equipment details
export const fetchBlockEquipmentDetails = createAsyncThunk(
  "equipment/fetchBlockEquipmentDetails",
  async (blockEquipId, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.get(`block_equipment/detail/${blockEquipId}/`),
      rejectWithValue,
    ),
);

// get a company equipment details by its uuid
export const fetchEquipmentDetails = createAsyncThunk(
  "equipment/fetchEquipmentDetails",
  async ({ companyUuid, equipUUID }, { rejectWithValue }) =>
    apiCall(
      () =>
        axiosInstance.get(
          `company_equipment/${companyUuid}/detail/${equipUUID}/`,
        ),
      rejectWithValue,
    ),
);

// fetch all equipment groups in a Company
export const fetchCompanyEquipmentGroups = createAsyncThunk(
  "equipment/fetchCompanyEquipmentGroups",
  async (companyId, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.get(`equipment-group/company/${companyId}/`),
      rejectWithValue,
    ),
);

// fetch an equipment group details by its uuid
export const fetchEquipmentGroupById = createAsyncThunk(
  "equipment/fetchEquipmentGroupById",
  async (equipGroupUUID, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.get(`equipment-group/${equipGroupUUID}/`),
      rejectWithValue,
    ),
);

export const fetchControlGroupById = createAsyncThunk(
  "equipment/fetchControlGroupById",
  async (equipGroupUUID, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.get(`equipment-group/${equipGroupUUID}/`),
      rejectWithValue,
    ),
);

// create an equipment group
export const createEquipmentGroup = createAsyncThunk(
  "equipment/createEquipmentGroup",
  async (data, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.post(`equipment-group/`, data),
      rejectWithValue,
    ),
);

// update an equipment group
export const updateEquipmentGroup = createAsyncThunk(
  "equipment/updateEquipmentGroup",
  async (data, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.patch(`equipment-group/`, data),
      rejectWithValue,
    ),
);

// delete an equipment group
export const deleteEquipmentGroup = createAsyncThunk(
  "equipment/deleteEquipmentGroup",
  async ({ equipGroupUUID }, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.delete(`equipment-group/${equipGroupUUID}/`),
      rejectWithValue,
    ),
);

// update equipment group items (equipments and sub-groups)
export const updateEquipmentGroupItems = createAsyncThunk(
  "equipment/updateEquipmentGroupItems",
  async (data, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const {
        equip_group_uuid: equipGroupUUID,
        add_equipment: addEquipment = [],
      } = data;

      // Throw an error if equipGroupUUID is not provided
      if (!equipGroupUUID) {
        return rejectWithValue("Missing equipment group UUID");
      }
      // Check if any of the equipment being added is already part of another group to prevent duplicates in the React Flow
      if (addEquipment.length) {
        const usedSet = new Set(
          state.equipment?.equipmentGroups?.flatMap((group) => [
            ...(group.equipment?.map((e) => e.co_equip_uuid) || []),
            ...(group.equipment_group_mappings?.map(
              (m) => m.company_equipment?.co_equip_uuid,
            ) || []),
          ]) || [],
        );

        // Check if any of the new equipment is already used
        const conflictingEquipment = addEquipment?.filter((uuid) =>
          usedSet.has(uuid),
        );

        if (conflictingEquipment?.length) {
          return rejectWithValue(
            `Equipment already in use: ${conflictingEquipment.join(", ")}`,
          );
        }
      }

      const parentGroup = await apiCall(
        () => axiosInstance.patch(`equipment-group/items/`, data),
        rejectWithValue,
      );

      // get the current_config for company equipment as it s not returned from the backend
      if (parentGroup?.equipment_group_mappings?.length) {
        const companyEquipment =
          state.companies?.company?.company_equipment || [];

        parentGroup.equipment_group_mappings.forEach((mapping) => {
          const equip = mapping.company_equipment;
          if (!equip) {
            return;
          }

          if (!equip.current_config) {
            const fallback = companyEquipment.find(
              (e) => e.co_equip_uuid === equip.co_equip_uuid,
            )?.current_config;

            if (!fallback) {
              return;
            }

            equip.current_config = fallback;
          }
        });
      }
      return { parentGroup };
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error updating equipment group",
      );
    }
  },
);

// Handle drag/drop equipment or equipment group node
export const moveNodeBetweenGroups = createAsyncThunk(
  "equipment/moveNodeBetweenGroups",
  async (
    { nodeId, fromGroupId, toGroupId, type = "equipment" },
    { dispatch, rejectWithValue },
  ) => {
    if (!nodeId || !toGroupId) {
      return rejectWithValue("Missing node or destination group");
    }

    const typeMap = {
      equipment: "equipment",
      equipmentGroup: "equipment_group",
    };

    const entityKey = typeMap[type];

    //  If moving from a group → remove first
    if (fromGroupId) {
      const removePayload = {
        equip_group_uuid: fromGroupId,
        [`remove_${entityKey}`]: [nodeId],
      };

      const removeResult = await dispatch(
        updateEquipmentGroupItems(removePayload),
      );

      if (updateEquipmentGroupItems.rejected.match(removeResult)) {
        return rejectWithValue("Failed removing from old group");
      }
    }

    //  Add to destination group
    const addPayload = {
      equip_group_uuid: toGroupId,
      [`add_${entityKey}`]: [nodeId],
    };

    const addResult = await dispatch(updateEquipmentGroupItems(addPayload));

    if (updateEquipmentGroupItems.rejected.match(addResult)) {
      return rejectWithValue("Failed adding to new group");
    }

    return true;
  },
);

// Handle adding or removing connection between two equipment and send appropriate websocket messages to update equipment settings
export const updateConnection = createAsyncThunk(
  "equipment/updateConnection",
  async (
    {
      sourceEquipment,
      targetEquipment,
      sourceHandle,
      targetHandle,
      action = "add", // "add" | "remove"
    },
    { getState, rejectWithValue, dispatch },
  ) => {
    const rootState = getState();

    const sourceStudioUUID = selectStudioUUIDByEquipmentUUID(
      rootState,
      sourceEquipment.co_equip_uuid,
    );

    const targetStudioUUID = selectStudioUUIDByEquipmentUUID(
      rootState,
      targetEquipment.co_equip_uuid,
    );

    if (!sourceStudioUUID || !targetStudioUUID) {
      return rejectWithValue(
        `Studio not found for ${sourceEquipment?.name} or ${targetEquipment?.name}`,
      );
    }

    const isRemove = action === "remove";

    const sourceIp =
      sourceEquipment?.current_config?.properties?.connection_details
        ?.ip_address?.eprop_val_uuid?.value;

    const targetIp =
      targetEquipment?.current_config?.properties?.connection_details
        ?.ip_address?.eprop_val_uuid?.value;

    const buildSourceSettings = () => ({
      destinations: {
        [sourceHandle]: {
          uuid: isRemove ? randomUUID : targetEquipment.co_equip_uuid,
          ip_address: isRemove ? "" : targetIp,
        },
      },
    });

    const buildTargetSettings = () => ({
      sources: {
        [targetHandle]: {
          uuid: isRemove ? randomUUID : sourceEquipment.co_equip_uuid,
          ip_address: isRemove ? "" : sourceIp,
        },
      },
    });

    await Promise.all([
      dispatch(
        wsStudioEquipmentDesiredState({
          studioUUID: sourceStudioUUID,
          equipmentUUID: sourceEquipment.co_equip_uuid,
          settings: buildSourceSettings(),
        }),
      ),
      dispatch(
        wsStudioEquipmentDesiredState({
          studioUUID: targetStudioUUID,
          equipmentUUID: targetEquipment.co_equip_uuid,
          settings: buildTargetSettings(),
        }),
      ),
    ]);

    return {
      source: sourceEquipment.co_equip_uuid,
      target: targetEquipment.co_equip_uuid,
      action,
    };
  },
);

// Remove connection between two equipment
export const removeConnection = createAsyncThunk(
  "equipment/removeConnection",
  async ({ connection, nodes }, { getState, rejectWithValue, dispatch }) => {
    const { source, target, sourceHandle, targetHandle } = connection;

    // ---------------------------------------------------
    // Find Source
    // ---------------------------------------------------
    const sourceNode = nodes.find((n) => n.id === source);

    // ---------------------------------------------------
    // Find Target
    // ---------------------------------------------------
    const targetNode = nodes.find((n) => n.id === target);

    // ---------------------------------------------------
    // Equipment or group not found case
    // ---------------------------------------------------
    if (!sourceNode && !targetNode) {
      return rejectWithValue("Source or target not found");
    }
    // ---------------------------------------------------
    // Equipment to Equipment removal connection
    // ---------------------------------------------------
    if (sourceNode?.type === "equipment" && targetNode?.type === "equipment") {
      return await dispatch(
        updateConnection({
          sourceEquipment: sourceNode.data.state,
          targetEquipment: targetNode.data.state,
          sourceHandle,
          targetHandle,
          action: "remove",
        }),
      );
    }

    // ---------------------------------------------------
    // Equipment Group to Equipment Group removal
    // ---------------------------------------------------
    if (
      sourceNode?.type === "equipmentGroup" &&
      targetNode?.type === "equipmentGroup"
    ) {
      const { outputs: sourceOutputs = [] } = sourceNode.data?.handles || {};
      const { inputs: targetInputs = [] } = targetNode.data?.handles || {};

      const sourceHandleObj = sourceOutputs.find((h) => h.id === sourceHandle);
      const targetHandleObj = targetInputs.find((h) => h.id === targetHandle);

      if (!sourceHandleObj || !targetHandleObj) {
        return rejectWithValue("Handles not found for source or target group");
      }

      const sourceChildren = sourceHandleObj.children || [];
      const targetChildren = targetHandleObj.children || [];

      const results = [];

      for (const sourceChild of sourceChildren) {
        const connection = sourceChild?.destination;

        if (!connection?.targetNodeId) {
          continue;
        }

        // Only remove connections that belong to this target group
        const targetChild = targetChildren.find(
          (tc) =>
            tc.nodeId === connection.targetNodeId &&
            tc.id === connection.targetHandleId,
        );

        if (!targetChild) {
          continue;
        }

        const sourceEquip = nodes.find((n) => n.id === sourceChild.nodeId);
        const targetEquip = nodes.find((n) => n.id === targetChild.nodeId);

        if (!sourceEquip || !targetEquip) {
          continue;
        }

        const result = await dispatch(
          updateConnection({
            sourceEquipment: sourceEquip.data.state,
            targetEquipment: targetEquip.data.state,
            sourceHandle: sourceChild.id,
            targetHandle: targetChild.id,
            action: "remove",
          }),
        );

        results.push(result);
      }

      return results;
    }
  },
);

// Handle adding a connection between two nodes (equipment or equipment group) in the React Flow and send appropriate websocket messages to update equipment settings based on the connection handles and protocols
export const addConnection = createAsyncThunk(
  "equipment/addConnection",
  async ({ connection, nodes }, { getState, rejectWithValue, dispatch }) => {
    const { source, target, sourceHandle, targetHandle } = connection;

    // ---------------------------------------------------
    // Find Source
    // ---------------------------------------------------
    const sourceNode = nodes.find((n) => n.id === source);

    // ---------------------------------------------------
    // Find Target
    // ---------------------------------------------------
    const targetNode = nodes.find((n) => n.id === target);

    // ---------------------------------------------------
    // Equipment or group not found case
    // ---------------------------------------------------
    if (!sourceNode && !targetNode) {
      return rejectWithValue("Source or target not found");
    }

    // ---------------------------------------------------
    // Equipment to Equipment connection
    // ---------------------------------------------------
    if (sourceNode?.type === "equipment" && targetNode?.type === "equipment") {
      // Find the handles
      const inputHandle = targetNode.data?.handles?.inputs?.find(
        (h) => h.id === targetHandle,
      );
      // if already connected to another equipment remove the old connection first (update equipment settings to remove source and destination from the settings based on the connection handles and protocol) before adding the new connection on success
      if (inputHandle?.connection?.sourceNodeId) {
        await dispatch(
          removeConnection({
            connection: {
              source: inputHandle.nodeId,
              sourceHandle: inputHandle.id,
              target: inputHandle?.connection?.sourceNodeId,
              targetHandle: inputHandle?.connection?.sourceHandleId,
            },
            nodes,
          }),
        );
      }

      return await dispatch(
        updateConnection({
          sourceEquipment: sourceNode.data.state,
          targetEquipment: targetNode.data.state,
          sourceHandle,
          targetHandle,
        }),
      );
    }

    // ---------------------------------------------------
    // Equipment Group to Equipment Group connection
    // ---------------------------------------------------
    if (
      sourceNode.type === "equipmentGroup" &&
      targetNode.type === "equipmentGroup"
    ) {
      // source > outputs
      const { outputs: sourceOutputs = [] } = sourceNode.data?.handles || {};
      // target > inputs
      const { inputs: targetInputs = [] } = targetNode.data?.handles || {};

      const sourceHandleObj = sourceOutputs.find((h) => h.id === sourceHandle);
      const targetHandleObj = targetInputs.find((h) => h.id === targetHandle);

      if (!sourceHandleObj || !targetHandleObj) {
        return rejectWithValue("Handles not found for source or target group");
      }

      const sourceChildren = (sourceHandleObj.children || []).filter(
        (c) => c.protocol,
      );

      const targetChildren = (targetHandleObj.children || []).filter(
        (c) => c.protocol,
      );

      const equipmentInsideFacility =
        selectAllEquipmentInsideGroups(getState());

      const matches = getMatchingProtocols(
        sourceChildren,
        targetChildren,
        equipmentInsideFacility,
      );

      if (!matches.length) {
        return rejectWithValue(
          "No matching protocol found between group children",
        );
      }

      if (matches.length !== targetChildren.length) {
        return rejectWithValue(
          "Not all source children could be connected. Aborting to prevent partial connections.",
        );
      }

      const results = [];

      for (const match of matches) {
        const sourceEquip = nodes.find((eq) => eq.id === match.sourceNodeId);
        const targetEquip = nodes.find((eq) => eq.id === match.targetNodeId);

        if (!sourceEquip || !targetEquip) {
          return rejectWithValue("Matched equipment not found in nodes");
        }
        // TODO : remove previous connection before adding ?
        const result = await dispatch(
          updateConnection({
            sourceEquipment: sourceEquip.data.state,
            targetEquipment: targetEquip.data.state,
            sourceHandle: match.sourceHandle,
            targetHandle: match.targetHandle,
          }),
        );

        results.push(result);
      }

      return results;
    }

    // ---------------------------------------------------
    // Optional: Equipment ↔ EquipmentGroup or vice versa
    // ---------------------------------------------------
    // TODO: handle connection between equipment and equipment group
    // similar logic thant equipment group to equipmen group
    // but should not remove previous connections before adding the new one instead returns an error if no handles available in the equipment group to connect to

    return rejectWithValue("Connection type not handled");
  },
);

/*
---------------------------------------------------------
Equipment motion controller store
---------------------------------------------------------
One controller per equipment device
This replaces throttleMap, moveTimeoutMap, lastPayloadMap
*/
const equipmentControllers = {};

/*
---------------------------------------------------------
Motion constants
---------------------------------------------------------
*/
// TODO : use async to send action message after websocket response on equipment settings update success

/*
---------------------------------------------------------
controlEquipment
Handles continuous updates + debounced move command
---------------------------------------------------------
*/
const MOVE_REPEAT_MS = 750; // delay between repeated move commands when dragging continuously for morail and elevation. Morail is not responding to stop action, increase value to reduce the number of move commands sent on dragging

export const controlEquipment = createAsyncThunk(
  "equipment/controlEquipment",
  async (
    { equipmentUUID, settings, action, timestamp },
    { getState, dispatch, rejectWithValue },
  ) => {
    const state = getState();
    const studioUUID = selectStudioUUIDByEquipmentUUID(state, equipmentUUID);

    if (!studioUUID || !equipmentUUID) {
      return rejectWithValue("Missing studioUUID or equipmentUUID");
    }

    if (!equipmentControllers[equipmentUUID]) {
      equipmentControllers[equipmentUUID] = {
        activeSession: null,
        lastMoveSent: 0,
        lastSettings: null,
        isMoving: false,
      };
    }

    const controller = equipmentControllers[equipmentUUID];
    const now = Date.now();

    const isDistanceDevice = Boolean(timestamp);

    /*
    =========================================================
    Distance devices (e.g. Morail, Elevation)
    =========================================================
    */
    if (isDistanceDevice) {
      if (!controller.activeSession) {
        controller.activeSession = timestamp;
      }

      if (settings) {
        dispatch(
          wsStudioEquipmentDesiredState({
            studioUUID,
            equipmentUUID,
            settings,
          }),
        );
      }

      if (action && now - controller.lastMoveSent > MOVE_REPEAT_MS) {
        dispatch(
          wsStudioEquipmentAction({
            studioUUID,
            equipmentUUID,
            action,
          }),
        );
        controller.lastMoveSent = now;
      }

      return { equipmentUUID };
    }

    /*
    =========================================================
    PTZ continuous devices (Pan/Tilt/Zoom)
    =========================================================
    */

    // Send settings ONLY if changed
    const settingsChanged =
      JSON.stringify(settings) !== JSON.stringify(controller.lastSettings);


    if (settings && settingsChanged) {
      dispatch(
        wsStudioEquipmentDesiredState({
          studioUUID,
          equipmentUUID,
          settings,
        }),
      );
      controller.lastSettings = settings;
    }

    // Send MOVE only once per drag
    if (action === "move" && (!controller.isMoving || settingsChanged)) {
      dispatch(
        wsStudioEquipmentAction({
          studioUUID,
          equipmentUUID,
          action: "move",
        }),
      );
      controller.isMoving = true;
    }

    return { equipmentUUID };
  },
);

/*
---------------------------------------------------------
controlEquipmentAction
Handles actions like STOP
---------------------------------------------------------
*/

export const controlEquipmentAction = createAsyncThunk(
  "equipment/controlEquipmentAction",
  async (
    { equipmentUUID, action },
    { getState, dispatch, rejectWithValue },
  ) => {
    const state = getState();
    const studioUUID = selectStudioUUIDByEquipmentUUID(state, equipmentUUID);

    if (!studioUUID || !equipmentUUID) {
      return rejectWithValue("Missing studioUUID or equipmentUUID");
    }

    const controller = equipmentControllers[equipmentUUID];
    dispatch(
      wsStudioEquipmentAction({
        studioUUID,
        equipmentUUID,
        action,
      }),
    );

    if (controller) {
      controller.activeSession = null;
      controller.isMoving = false;
      controller.lastSettings = null;
    }

    return { equipmentUUID, action };
  },
);

// update a shot console's button assignments
export const updateButtonAssignments = createAsyncThunk(
  "equipment/updateButtonAssignments",
  async (
    { shotConsoleUUID, payload },
    { getState, rejectWithValue, dispatch },
  ) => {
    if (!shotConsoleUUID) {
      const message = `Shot Console invalid: ${shotConsoleUUID}`;
      return rejectWithValue(message);
    }
    const studioUUID = selectStudioUUIDByEquipmentUUID(
      getState(),
      shotConsoleUUID,
    );
    if (!studioUUID) {
      const message = `Studio not found for Shot Console ${shotConsoleUUID}`;
      return rejectWithValue(message);
    }
    dispatch(
      wsStudioEquipmentDesiredState({
        studioUUID,
        equipmentUUID: shotConsoleUUID,
        settings: {
          button_config: payload,
        },
      }),
    );
  },
);

// ---------------------------------------------------
// Equipment Group Types
// ---------------------------------------------------

// fetch all equipment group types
export const fetchEquipmentGroupTypes = createAsyncThunk(
  "equipment/fetchEquipmentGroupTypes",
  async (_, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get(`equipment-group/types/`), rejectWithValue),
);

// Create a equipment group type
export const createEquipmentGroupType = createAsyncThunk(
  "equipment/createEquipmentGroupType",
  async (data, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.post(`equipment-group/types/`, data),
      rejectWithValue,
    ),
);

// Update a equipment group type
export const updateEquipmentGroupType = createAsyncThunk(
  "equipment/updateEquipmentGroupType",
  async (data, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.patch(`equipment-group/types/`, data),
      rejectWithValue,
    ),
);

// Delete a equipment group type
export const deleteEquipmentGroupType = createAsyncThunk(
  "equipment/deleteEquipmentGroupType",
  async (equipTypeId, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.delete(`equipment-group/types/${equipTypeId}/`),
      rejectWithValue,
    ),
);

// ---------------------------------------------------
// Equipment Group Type Rule
// ---------------------------------------------------

// get equipment group type rule
export const fetchEquipmentGroupTypeRule = createAsyncThunk(
  "equipment/fetchEquipmentGroupTypeRule",
  async (equipTypeRuleId, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.get(`equipment-group/type/rule/${equipTypeRuleId}/`),
      rejectWithValue,
    ),
);

// Create a equipment group type rule
export const createEquipmentGroupTypeRule = createAsyncThunk(
  "equipment/createEquipmentGroupTypeRule",
  async (data, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.post(`equipment-group/type/rule/`, data),
      rejectWithValue,
    ),
);

// Update a equipment group type rule
export const updateEquipmentGroupTypeRule = createAsyncThunk(
  "equipment/updateEquipmentGroupTypeRule",
  async (data, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.patch(`equipment-group/type/rule/`, data),
      rejectWithValue,
    ),
);

// Delete a equipment group type rule
export const deleteEquipmentGroupTypeRule = createAsyncThunk(
  "equipment/deleteEquipmentGroupTypeRule",
  async (equipTypeRuleId, { rejectWithValue }) =>
    apiCall(
      () =>
        axiosInstance.delete(`equipment-group/type/rule/${equipTypeRuleId}/`),
      rejectWithValue,
    ),
);

// ---------------------------------------------------
// Equipment Group Type Rule Set
// ---------------------------------------------------

// Get all equipment Group type rule sets
export const fetchEquipmentGroupTypeRuleSets = createAsyncThunk(
  "equipment/fetchEquipmentGroupTypeRuleSets",
  async (_, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.get(`equipment-group/type/rule/set/`),
      rejectWithValue,
    ),
);

// Create a equipment group type rule set
export const createEquipmentGroupTypeRuleSet = createAsyncThunk(
  "equipment/createEquipmentGroupTypeRuleSet",
  async (data, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.post(`equipment-group/type/rule/set/`, data),
      rejectWithValue,
    ),
);

// Update a equipment group type rule set
export const updateEquipmentGroupTypeRuleSet = createAsyncThunk(
  "equipment/updateEquipmentGroupTypeRuleSet",
  async (data, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.patch(`equipment-group/type/rule/set/`, data),
      rejectWithValue,
    ),
);

// Delete a equipment group type rule set
export const deleteEquipmentGroupTypeRuleSet = createAsyncThunk(
  "equipment/deleteEquipmentGroupTypeRuleSet",
  async (equipTypeRuleSetId, { rejectWithValue }) =>
    apiCall(
      () =>
        axiosInstance.delete(
          `equipment-group/type/rule/set/${equipTypeRuleSetId}/`,
        ),
      rejectWithValue,
    ),
);

// ---------------------------------------------------
// Preset
// ---------------------------------------------------

// get all presets for a equipment group
export const fetchPresets = createAsyncThunk(
  "equipment/fetchPresets",
  async (equipGroupUUID, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.get(`preset/equip-group/${equipGroupUUID}/`),
      rejectWithValue,
    ),
);

// create a preset for an equipment group
export const createPreset = createAsyncThunk(
  "equipment/createPreset",
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`preset/`, data), rejectWithValue),
);

// update a preset
export const updatePreset = createAsyncThunk(
  "equipment/updatePreset",
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.patch(`preset/`, data), rejectWithValue),
);

// delete a preset
export const deletePreset = createAsyncThunk(
  "equipment/deletePreset",
  async (presetId, { rejectWithValue }) =>
    apiCall(() => axiosInstance.delete(`preset/${presetId}/`), rejectWithValue),
);

// apply preset
export const applyPreset = createAsyncThunk(
  "equipment/applyPreset",
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`preset/apply/`, data), rejectWithValue),
);

// stop a preset
export const stopPreset = createAsyncThunk(
  "equipment/stopPreset",
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`preset/stop/`, data), rejectWithValue),
);

// stop all preset movements in a studio
export const stopAllPresetsInStudio = createAsyncThunk(
  "equipment/stopAllPresetsInStudio",
  async (data, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.post(`preset/stop-all/`, data),
      rejectWithValue,
    ),
);

// update preset image
export const updatePresetImage = createAsyncThunk(
  "equipment/updatePresetImage",
  async (
    { presetId, imgType = "Large", lastUpdated = "", defaultImage = "" },
    { rejectWithValue },
  ) => {
    try {
      const result = await uploadImageAndRefresh({
        id: presetId,
        sourceId: null,
        imgType,
        endpointPrefix: "preset",
        sourceEndpointPrefix: "preset", // null?
        lastUpdated,
        defaultImage,
      });
      return result;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.detail?.[0]?.msg ||
        error?.message ||
        "Failed to update block image",
      );
    }
  },
);

// ---------------------------------------------------
// Preset Sequence
// ---------------------------------------------------

// get all preset sequences for a EquipmentGroup
export const fetchPresetSequencesForGroup = createAsyncThunk(
  "equipment/fetchPresetSequencesForGroup",
  async (equipGroupUUID, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.get(`preset/sequence/equip-group/${equipGroupUUID}/`),
      rejectWithValue,
    ),
);

// get a preset sequence by id
export const fetchPresetSequenceById = createAsyncThunk(
  "equipment/fetchPresetSequenceById",
  async (presetSequenceId, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.get(`preset/sequence/${presetSequenceId}/`),
      rejectWithValue,
    ),
);

// create a preset sequence
export const createPresetSequence = createAsyncThunk(
  "equipment/createPresetSequence",
  async (data, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.post(`preset/sequence/`, data),
      rejectWithValue,
    ),
);

// update a preset sequence
export const updatePresetSequence = createAsyncThunk(
  "equipment/updatePresetSequence",
  async (data, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.patch(`preset/sequence/`, data),
      rejectWithValue,
    ),
);

// delete a preset sequence
export const deletePresetSequence = createAsyncThunk(
  "equipment/deletePresetSequence",
  async (presetSequenceId, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.delete(`preset/sequence/${presetSequenceId}/`),
      rejectWithValue,
    ),
);

// apply a preset sequence
export const applyPresetSequence = createAsyncThunk(
  "equipment/applyPresetSequence",
  async (data, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.post(`preset/sequence/apply/`, data),
      rejectWithValue,
    ),
);

// stop a preset sequence
export const stopPresetSequence = createAsyncThunk(
  "equipment/stopPresetSequence",
  async (data, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.post(`preset/sequence/stop/`, data),
      rejectWithValue,
    ),
);

// Create or update preset sequence image
export const updatePresetSequenceImage = createAsyncThunk(
  "equipment/updatePresetSequenceImage",
  async (
    {
      presetSequenceId,
      imgType = "Large",
      lastUpdated = "",
      defaultImage = "",
    },
    { rejectWithValue },
  ) => {
    try {
      const result = await uploadImageAndRefresh({
        id: presetSequenceId,
        sourceId: null,
        imgType,
        endpointPrefix: "preset/sequence",
        sourceEndpointPrefix: null,
        lastUpdated,
        defaultImage,
      });
      return result;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.detail?.[0]?.msg ||
        error?.message ||
        "Failed to update block image",
      );
    }
  },
);
// ---------------------------------------------------
// Preset Sequence Step
// ---------------------------------------------------

// get all preset sequence steps for a preset sequence by id
export const fetchPresetSequenceStepsForSequence = createAsyncThunk(
  "equipment/fetchPresetSequenceStepsForSequence",
  async (presetSequenceId, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.get(`preset/sequence/steps/${presetSequenceId}/`),
      rejectWithValue,
    ),
);

// get a preset sequence step by id
export const fetchPresetSequenceStepById = createAsyncThunk(
  "equipment/fetchPresetSequenceStepById",
  async (presetSequenceStepId, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.get(`preset/sequence/step/${presetSequenceStepId}/`),
      rejectWithValue,
    ),
);

// create a preset sequence step
export const createPresetSequenceStep = createAsyncThunk(
  "equipment/createPresetSequenceStep",
  async (data, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.post(`preset/sequence/step/`, data),
      rejectWithValue,
    ),
);

// update a preset sequence step
export const updatePresetSequenceStep = createAsyncThunk(
  "equipment/updatePresetSequenceStep",
  async (data, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.patch(`preset/sequence/step/`, data),
      rejectWithValue,
    ),
);

// Deleete Preset sequence step
export const deletePresetSequenceStep = createAsyncThunk(
  "equipment/deletePresetSequenceStep",
  async (presetSequenceStepId, { rejectWithValue }) =>
    apiCall(
      () =>
        axiosInstance.delete(`preset/sequence/step/${presetSequenceStepId}/`),
      rejectWithValue,
    ),
);
